import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { DateRangePicker, DateRangePickerProps, Range, RangeKeyDict } from "react-date-range";
import styles from "./styles.module.scss";
import { isSameDay } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Button, Popover, PopoverContent, PopoverTrigger, Stack } from "@altimateai/lego";
import { uniqueId } from "lodash";
import { StaticDateRangeKeys, getStaticRanges } from "./staticRanges";
import InlineTrigger, { TriggerProps } from "./InlineTrigger";
import DefaultTrigger from "./DefaultTrigger";

type DateRangeVariant = "default" | "inline";

interface Props extends DateRangePickerProps {
  startDate?: Date;
  endDate?: Date;
  id?: string;
  wrapperClass?: string;
  onDateRangeSelect: (startDate: Date, endDate: Date) => void;
  availableStaticRanges?: string[];
  disableCalendarSelection?: boolean;
  minDate?: Date;
  showTimezone?: boolean;
  label?: string;
  variant?: DateRangeVariant;
}

const DateRange = ({
  wrapperClass,
  onDateRangeSelect,
  startDate,
  endDate,
  availableStaticRanges = Object.values(StaticDateRangeKeys).filter(
    range => range !== StaticDateRangeKeys.LAST_30_DAYS
  ),
  disableCalendarSelection,
  minDate,
  showTimezone = false,
  label,
  variant = "default",
  ...rest
}: Props) => {
  const id = useMemo(() => uniqueId("date-picker-"), []);
  const ref = useRef<DateRangePicker | null>(null);
  const [state, setState] = useState<Range[]>([
    {
      startDate: startDate || new Date(),
      endDate: endDate || new Date(),
      key: "selection",
    },
  ]);
  const [isOpen, setIsOpen] = useState(false);
  const close = () => setIsOpen(false);
  useEffect(() => {
    setState([
      {
        startDate: startDate || new Date(),
        endDate: endDate || new Date(),
        key: "selection",
      },
    ]);
  }, [startDate, endDate]);

  const staticRanges = getStaticRanges(availableStaticRanges);

  const calculateDateRangeFromTwoDates = (
    beginDate: dayjs.Dayjs,
    endDate: dayjs.Dayjs
  ): string | false => {
    const selectedRange = staticRanges.find(r =>
      r.isSelected({
        startDate: beginDate.toDate(),
        endDate: endDate.toDate(),
        key: "selection",
      })
    );
    return selectedRange?.label ?? false;
  };

  const handleDatePick = (item: RangeKeyDict, close: () => void) => {
    setState([item.selection]);

    if (
      item.selection.startDate &&
      item.selection.endDate &&
      calculateDateRangeFromTwoDates(dayjs(item.selection.startDate), dayjs(item.selection.endDate))
    ) {
      close();
      onDateRangeSelect(item.selection.startDate, item.selection.endDate);
    }
  };

  const handleApply = (close: () => void) => {
    if (state.length) {
      const [selection] = state;
      if (selection.startDate && selection.endDate) {
        close();
        onDateRangeSelect(selection.startDate, selection.endDate);
      }
    }
  };

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  const props = disableCalendarSelection
    ? { ...rest, maxDate: new Date(), minDate: new Date() }
    : rest;

  const staticRangeLabel: string | false = calculateDateRangeFromTwoDates(
    dayjs(startDate),
    dayjs(endDate)
  );

  const renderDateRange = () => {
    if (!startDate || !endDate) return null;
    return (
      <Stack aria-label="Date range">
        <div>{startDate.toLocaleDateString("en-US", options)}</div>
        {!isSameDay(startDate, endDate) && (
          <>
            <div>-</div>
            <div>{endDate?.toLocaleDateString("en-US", options)}</div>
          </>
        )}
      </Stack>
    );
  };

  const triggerProps: TriggerProps = {
    id,
    startDate,
    endDate,
    isOpen,
    wrapperClass,
    showTimezone,
    label,
    staticRangeLabel,
    renderDateRange,
  };

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger>
          {variant === "inline" ? (
            <InlineTrigger {...triggerProps} />
          ) : (
            <DefaultTrigger {...triggerProps} />
          )}
        </PopoverTrigger>
        <PopoverContent align="end" className="al-w-[900px]">
          <Stack direction="column">
            <DateRangePicker
              ref={ref}
              // disable add/substract days input
              inputRanges={[]}
              renderStaticRangeLabel={item => {
                const isSelected = item.isSelected(state[0]);
                return (
                  <span
                    aria-label={`Select ${item.label}`}
                    aria-selected={isSelected}
                    data-selected={isSelected}
                  >
                    {item.label}
                  </span>
                );
              }}
              onChange={(item: RangeKeyDict) => handleDatePick(item, close)}
              // @ts-ignore prop exists
              showSelectionPreview
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={state}
              staticRanges={staticRanges}
              direction="horizontal"
              showDateDisplay={false}
              minDate={minDate}
              {...props}
            />
            <div className={styles.apply_div}>
              <Button size="sm" onClick={() => handleApply(close)}>
                Apply
              </Button>
            </div>
          </Stack>
        </PopoverContent>
      </Popover>
    </>
  );
};

export { DateRange };
export type { DateRangeVariant };
export { StaticDateRangeKeys } from "./staticRanges";
